import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    doc,
    getDoc,
    onSnapshot,
    collection,
    query,
    where,
    orderBy,
    limit,
} from "firebase/firestore";
import { dbWeb } from "../firebaseConfig";
import { getHariLong, getTarikh } from "../utils/formatHelpers";
import { getTitleFromRaw, parseBlocks, arabicRatio } from "../utils/tazkirahUtils";
import { FiMinus, FiPlus, FiShare2 } from "react-icons/fi";
import PageBackBar from "../components/PageBackBar";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "../constants/appLinks";

import "../styles/Tazkirah.css";

const TEXT_SIZES = [0.9, 1, 1.15, 1.3];

const fmtDate = (ts) => {
    if (!ts) return "";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return `${getHariLong(d)}, ${getTarikh(d)}`;
};

export default function Tazkirah() {
    const { slug } = useParams();

    const [item, setItem] = useState(null);
    const [list, setList] = useState([]);
    const [textSizeIndex, setTextSizeIndex] = useState(1);
    const [shareStatus, setShareStatus] = useState("");

    // load doc
    useEffect(() => {
        (async () => {
            const ref = doc(dbWeb, "tazkirah", slug);
            const snap = await getDoc(ref);
            if (!snap.exists()) return;
            setItem({ id: snap.id, ...snap.data() });
        })();
    }, [slug]);

    // load list published (sidebar + prev/next)
    useEffect(() => {
        const q = query(
            collection(dbWeb, "tazkirah"),
            where("status", "==", "published"),
            orderBy("publishedAt", "desc"),
            limit(50)
        );

        return onSnapshot(q, (snap) => {
            setList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });
    }, []);

    const title = useMemo(() => {
        if (!item) return "";
        return item.title || getTitleFromRaw(item.raw);
    }, [item]);

    const shareArticle = async () => {
        const shareData = {
            title: title || "Tazkirah",
            text: title || "Tazkirah",
            url: window.location.href,
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
                return;
            }

            await navigator.clipboard.writeText(window.location.href);
            setShareStatus("Pautan disalin");
            window.setTimeout(() => setShareStatus(""), 2200);
        } catch (error) {
            if (error?.name !== "AbortError") {
                setShareStatus("Perkongsian tidak tersedia");
                window.setTimeout(() => setShareStatus(""), 2200);
            }
        }
    };

    const idx = useMemo(() => list.findIndex((x) => x.id === slug), [list, slug]);
    const prev = idx >= 0 ? list[idx + 1] : null; // sebab desc
    const next = idx > 0 ? list[idx - 1] : null;

    if (!item) return null;

    return (
        <div className="taz-page">
            <PageBackBar title="Tazkirah" />


            <div className="pg-hero">
                {/* MAIN */}
                <article className="taz-article">
                    <div className="taz-head">
                        <h1 className="pg-h1 taz-title">{title}</h1>

                        <div className="taz-meta">
                            <span className="taz-date">{fmtDate(item.publishedAt)}</span>
                            <div className="taz-reader-tools" aria-label="Kawalan bacaan">
                                <button
                                    type="button"
                                    className="taz-reader-button"
                                    onClick={() => setTextSizeIndex((value) => Math.max(0, value - 1))}
                                    disabled={textSizeIndex === 0}
                                    aria-label="Kecilkan saiz teks"
                                    title="Kecilkan teks"
                                >
                                    <FiMinus />
                                </button>
                                <span className="taz-reader-letter" aria-hidden="true">A</span>
                                <button
                                    type="button"
                                    className="taz-reader-button"
                                    onClick={() => setTextSizeIndex((value) => Math.min(TEXT_SIZES.length - 1, value + 1))}
                                    disabled={textSizeIndex === TEXT_SIZES.length - 1}
                                    aria-label="Besarkan saiz teks"
                                    title="Besarkan teks"
                                >
                                    <FiPlus />
                                </button>
                                <span className="taz-reader-divider" aria-hidden="true" />
                                <button
                                    type="button"
                                    className="taz-reader-button"
                                    onClick={shareArticle}
                                    aria-label="Kongsi tazkirah"
                                    title="Kongsi tazkirah"
                                >
                                    <FiShare2 />
                                </button>
                            </div>
                            <span className="taz-share-status" role="status" aria-live="polite">
                                {shareStatus}
                            </span>
                        </div>
                    </div>

                    <div
                        className="taz-content"
                        style={{ "--taz-font-scale": TEXT_SIZES[textSizeIndex] }}
                    >
                        {parseBlocks(item.raw || "")
                            .slice(1) // buang tajuk
                            .flatMap((b, bi) => {
                                let prevWasArabic = false;

                                return b.text
                                    .split(/\r?\n/)
                                    .filter(Boolean)
                                    .map((line, li) => {
                                        const isAr = arabicRatio(line) > 0.3;
                                        const isTranslation = !isAr && prevWasArabic;

                                        prevWasArabic = isAr;

                                        return (
                                            <p
                                                key={`${bi}-${li}`}
                                                className={`taz-p ${isAr ? "ar" : ""} ${isTranslation ? "tr" : ""}`}
                                                dir={isAr ? "rtl" : "ltr"}
                                                lang={isAr ? "ar" : "ms"}
                                                style={{ whiteSpace: "pre-wrap" }}
                                            >
                                                {line}
                                            </p>
                                        );
                                    });
                            })}
                        <aside className="taz-app-promo" aria-label="Muat turun aplikasi Etikaf">
                            <img className="taz-app-icon" src="/assets/e-icon.png" alt="" />
                            <div className="taz-app-copy">
                                <strong>Baca melalui aplikasi Etikaf</strong>
                                <span>Tazkirah dan maklumat masjid sentiasa dekat dengan anda.</span>
                                <div className="taz-app-stores">
                                    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Muat turun Etikaf di App Store">
                                        <img src="/assets/apple.png" alt="Muat turun di App Store" />
                                    </a>
                                    <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" aria-label="Muat turun Etikaf di Google Play">
                                        <img src="/assets/google.png" alt="Dapatkan di Google Play" />
                                    </a>
                                </div>
                            </div>
                        </aside>
                    </div>

                    {/* Navigator prev/next */}
                    <div className="taz-nav">
                        {prev ? (
                            <Link className="taz-navlink" to={`/tazkirah/${prev.id}`}>
                                ← {prev.title || prev.id}
                            </Link>
                        ) : (
                            <span />
                        )}

                        {next ? (
                            <Link className="taz-navlink" to={`/tazkirah/${next.id}`}>
                                {next.title || next.id} →
                            </Link>
                        ) : (
                            <span />
                        )}
                    </div>
                </article>

                {/* SIDEBAR */}
                <aside className="pg-side-card taz-side">
                    <div className="pg-side-head">
                        <div>
                            <div className="pg-side-title">Tazkirah</div>
                            <div className="pg-side-sub">Senarai artikel terkini</div>
                        </div>
                    </div>

                    <div className="pg-side-body">
                        <div className="taz-side-list">
                            {list.map((t) => (
                                <Link
                                    key={t.id}
                                    to={`/tazkirah/${t.id}`}
                                    className={`taz-side-item ${t.id === slug ? "active" : ""}`}
                                >
                                    <div className="taz-side-item-title">{t.title || t.id}</div>
                                    <div className="taz-side-item-date">{fmtDate(t.publishedAt)}</div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </aside>
            </div>

        </div>
    );
}
